import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, ZoomIn, ZoomOut, X } from "lucide-react";

const VIEW = 272;   // 取景框边长（px）
const OUT = 320;    // 输出方图边长（px）

/* 头像方形裁剪：拖动平移 · 滚轮 / 滑杆缩放 → JPEG Blob（由调用方分片上传到对象存储） */
export default function AvatarCropDialog({ file, zh, busy, onDone, onCancel }) {
  const T = (z, e) => (zh ? z : e);
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const [min, setMin] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => { const m = VIEW / Math.min(im.width, im.height); setImg(im); setMin(m); setScale(m); setOff({ x: (VIEW - im.width * m) / 2, y: (VIEW - im.height * m) / 2 }); };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clamp = (o, s) => ({ x: Math.min(0, Math.max(VIEW - img.width * s, o.x)), y: Math.min(0, Math.max(VIEW - img.height * s, o.y)) });
  const zoomTo = (s) => {
    if (!img) return;
    const ns = Math.min(min * 4, Math.max(min, s));
    const cx = VIEW / 2, cy = VIEW / 2;   // 以取景框中心为锚点缩放
    setOff((o) => clamp({ x: cx - ((cx - o.x) / scale) * ns, y: cy - ((cy - o.y) / scale) * ns }, ns));
    setScale(ns);
  };
  const onDown = (e) => { drag.current = { x: e.clientX - off.x, y: e.clientY - off.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (!drag.current || !img) return; setOff(clamp({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }, scale)); };
  const onUp = () => { drag.current = null; };
  const onWheel = (e) => { e.preventDefault(); zoomTo(scale * (e.deltaY < 0 ? 1.08 : 0.92)); };

  const confirm = () => {
    if (!img) return;
    const cv = document.createElement("canvas");
    cv.width = OUT; cv.height = OUT;
    const k = OUT / VIEW;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(img, off.x * k, off.y * k, img.width * scale * k, img.height * scale * k);
    cv.toBlob((b) => b && onDone(b), "image/jpeg", 0.88);
  };

  if (!file) return null;
  return createPortal(
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="avatar-crop-dialog" onClick={busy ? undefined : onCancel}>
      <div className="w-full max-w-sm rounded-2xl bg-white border border-slate-200 shadow-2xl p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-800">{T("裁剪头像", "Crop avatar")}</h3>
          <button type="button" data-testid="avatar-crop-close" onClick={onCancel} disabled={busy} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-2">{T("拖动图片调整位置，滚轮或滑杆缩放；保存为方形头像。", "Drag to position, scroll or slide to zoom. Saved as a square avatar.")}</p>
        <div className="mx-auto relative rounded-2xl overflow-hidden bg-slate-900 touch-none select-none cursor-grab active:cursor-grabbing ring-2 ring-[#7B5CFF]/50" style={{ width: VIEW, height: VIEW }}
          data-testid="avatar-crop-viewport" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onWheel={onWheel}>
          {img ? <img src={img.src} alt="" draggable={false} className="absolute max-w-none pointer-events-none" style={{ left: off.x, top: off.y, width: img.width * scale, height: img.height * scale }} />
            : <div className="h-full w-full flex items-center justify-center text-white/60"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          <div className="absolute inset-0 pointer-events-none rounded-full border-2 border-white/70 shadow-[0_0_0_999px_rgba(15,23,42,0.35)]" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button type="button" data-testid="avatar-crop-zoom-out" onClick={() => zoomTo(scale * 0.9)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100"><ZoomOut className="h-4 w-4" /></button>
          <input type="range" data-testid="avatar-crop-zoom" min={min} max={min * 4} step={min / 50} value={scale} onChange={(e) => zoomTo(parseFloat(e.target.value))} className="flex-1 accent-[#7B5CFF]" disabled={!img} />
          <button type="button" data-testid="avatar-crop-zoom-in" onClick={() => zoomTo(scale * 1.1)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100"><ZoomIn className="h-4 w-4" /></button>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" data-testid="avatar-crop-cancel" onClick={onCancel} disabled={busy} className="text-xs font-semibold rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100">{T("取消", "Cancel")}</button>
          <button type="button" data-testid="avatar-crop-save" onClick={confirm} disabled={!img || busy} className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-xl px-4 py-2 bg-gradient-to-r from-[#4F6BFF] to-[#7B5CFF] text-white shadow-md shadow-indigo-500/20 disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{busy ? T("上传中…", "Uploading…") : T("保存头像", "Save avatar")}
          </button>
        </div>
      </div>
    </div>, document.body);
}
